import React, { memo } from 'react';
import styled from 'styled-components';
import { useTranslation, TranslateFn } from '@blocs.i18n';
import Icon from '@minecraft.icon';
import { CoreTheme } from '@minecraft.themes';
import { ProfileMeasurementsHeightFragment, ProfileMeasurementsWeightFragment } from '@minecraft.graphql-operations';
import { gqlHeight, gqlWeight } from '@minecraft.graphql-types';

type ProfileMeasurementsCompatType = {
  height: gqlHeight | ProfileMeasurementsHeightFragment;
  weight: gqlWeight | ProfileMeasurementsWeightFragment;
};

/* ----- Styled Components----- */
const StyledProfileMeasurements = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 0.75rem;
  font-weight: 400;
`;

const StyledIcon = styled(Icon)`
  width: 0.875rem;
  height: 0.875rem;
`;

/* ----- Helpers ----- */
function getHeightReadable(
  height: ProfileMeasurementsCompatType['height'],
  isImperial: boolean,
  t: TranslateFn
): string {
  if (!isImperial) return `${height.cm || height.METRIC_CENTIMETERS} ${t('common:measurement.unit.cm')}`;

  return String(height.IMPERIAL_FEET_INCHES)
    .replace("'", ` ${t('common:measurement.unit.ft')} `)
    .replace('"', ` ${t('common:measurement.unit.in')}`);
}

function getWeightReadable(
  weight: ProfileMeasurementsCompatType['weight'],
  isImperial: boolean,
  t: TranslateFn
): string {
  return isImperial
    ? `${weight.IMPERIAL_LBS || weight.lbs} ${t('common:measurement.unit.lbs')}`
    : `${weight.kgRounded || weight.kg} ${t('common:measurement.unit.kg')}`;
}

export function getProfileMeasurementsInfoString(
  profileMeasurements: ProfileMeasurementsCompatType,
  isImperial: boolean,
  t: TranslateFn
): string {
  const { height, weight } = profileMeasurements;

  const heightReadable: string = height ? getHeightReadable(height, isImperial, t) : '';
  const weightReadable: string = weight ? getWeightReadable(weight, isImperial, t) : '';
  const showComma = Boolean(heightReadable && weightReadable);

  return `${heightReadable}${showComma ? ', ' : ''}${weightReadable}`;
}

/* ----- Main Component ----- */

const ProfileMeasurements: React.FC<{
  profileMeasurements: ProfileMeasurementsCompatType;
  isImperial?: boolean;
  isProfileDeactivated?: boolean;
}> = ({ profileMeasurements, isImperial, isProfileDeactivated }) => {
  const { t } = useTranslation();
  const showInImperial: boolean =
    isImperial ??
    (profileMeasurements?.height?.showInImperialUI === true || profileMeasurements?.weight?.showInMetricUI === false);
  const infoString: string = getProfileMeasurementsInfoString(profileMeasurements, showInImperial, t);
  const iconColor = isProfileDeactivated ? CoreTheme.colors.primary.white : CoreTheme.colors.primary.darkGrey;

  return infoString ? (
    <StyledProfileMeasurements data-testid="profile-measurements-container">
      <StyledIcon name="measurements" color={iconColor} />
      <span>{infoString}</span>
    </StyledProfileMeasurements>
  ) : null;
};

export default memo(ProfileMeasurements);
